import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useChecklist from 'data/react-query/queries/useChecklist';
import useCanAssessProperty from 'data/react-query/queries/useCanAssessProperty';
import useLocalAssessment from 'data/react-query/queries/useLocalAssessment';
import useRemoteAssessment from 'data/react-query/queries/useRemoteAssessment';
import usePhotoSession from 'data/react-query/queries/usePhotoSession';
import useAssessmentV2Enabled from 'data/react-query/queries/useAssessmentV2Enabled';
import useSaveLocalAssessment from 'data/react-query/mutations/useSaveLocalAssessment';
import { IS_PROPERTY_ASSESSMENT_V2_ENABLED_FEATURE_FLAG } from 'data/feature-flags/constants';
import { logFailure } from 'utils/log';

import { loadAssessment, updateIsAssessmentV2Enabled } from '../actions';
import {
  makeSelectAssessment,
  makeSelectHasLoadedAssessment,
} from '../selectors';
import { convertToAssessment } from '../data/propertyAssessment';

const useAssessment = (photoSessionId, onLoad) => {
  const dispatch = useDispatch();

  const propertyAssessment = useSelector(makeSelectAssessment(photoSessionId));
  const hasLoaded = useSelector(makeSelectHasLoadedAssessment(photoSessionId));

  const { mutate: saveLocalAssessment } =
    useSaveLocalAssessment(photoSessionId);

  const { data: photoSession } = usePhotoSession(photoSessionId);

  const { data: isAssessmentV2Enabled } = useAssessmentV2Enabled(
    IS_PROPERTY_ASSESSMENT_V2_ENABLED_FEATURE_FLAG,
    false,
    { city: photoSession?.house?.city },
    {
      refetchOnWindowFocus: false,
      enabled: !!photoSession?.house,
      onSuccess: (data) => {
        dispatch(updateIsAssessmentV2Enabled(photoSessionId, data));
      },
    },
  );

  const syncData = useCallback(
    (updatedAssessment, updatedChecklist) => {
      dispatch(
        loadAssessment({
          photoSessionId,
          assessment: updatedAssessment,
          checklist: updatedChecklist,
          onUpdate: () => {
            if (onLoad) {
              onLoad();
            }
          },
          isAssessmentV2Enabled,
        }),
      );
    },
    [photoSessionId, onLoad, isAssessmentV2Enabled],
  );

  const { data: checklist } = useChecklist(photoSessionId, {
    refetchOnWindowFocus: false,
  });

  const {
    data: canAssessProperty = false,
    isLoading: isLoadingCanAssessProperty,
  } = useCanAssessProperty(photoSessionId);

  const { data: remoteAssessment, error: remoteAssessmentError } =
    useRemoteAssessment(photoSessionId, {
      enabled: Boolean(checklist && canAssessProperty && photoSessionId),
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        const assessment = convertToAssessment(data);

        syncData(assessment, checklist);

        saveLocalAssessment({ assessment });
      },
      onError: () => {
        logFailure({ message: 'Error while fetching property assessment' });
      },
    });

  useLocalAssessment(photoSessionId, {
    enabled: Boolean(checklist && remoteAssessmentError),
    refetchOnWindowFocus: false,
    onSettled: (data) => {
      if (!remoteAssessment?.completed) {
        syncData(data?.assessment, checklist);
      }
    },
  });

  const isLoading =
    isLoadingCanAssessProperty || canAssessProperty ? !hasLoaded : false;

  return {
    isLoading,
    data: canAssessProperty ? propertyAssessment : undefined,
  };
};

export default useAssessment;
